import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Typography,
  Popconfirm,
  Spin,
  Space,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ChatComponent } from '../../../../../components';
import { SearchSelectField } from '@src/components/shared/fields/SearchSelectField';
import { useClinicInfo } from './hooks/useClinicInfo';
import {
  ComplaintItem,
  DiagnosisItem,
  TreatmentSuggestionItem,
  FutureSuggestionItem,
} from './types';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ClinicInfoTabProps {
  patient: any;
}

export const ClinicInfoTab: React.FC<ClinicInfoTabProps> = ({ patient }) => {
  // Input states
  const [complaintInput, setComplaintInput] = useState('');
  const [diagnosisInput, setDiagnosisInput] = useState('');
  const [treatmentInput, setTreatmentInput] = useState('');
  const [futureSuggestionInput, setFutureSuggestionInput] = useState('');

  // Get section height for each of the 4 clinic-info cards (2 rows in left panel)
  const getSectionHeight = () => {
    if (typeof window === 'undefined') return '280px';
    const height = window.innerHeight;
    const reservedHeight = 280;
    const availableHeight = height - reservedHeight;
    return `${Math.max(180, (availableHeight - 12) / 2)}px`;
  };

  // Edit states
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingType, setEditingType] = useState<
    | 'complaint'
    | 'diagnosis'
    | 'treatment-suggestion'
    | 'future-suggestion'
    | null
  >(null);

  // Quick add states for each section
  const [quickAddVisible, setQuickAddVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const [quickAddText, setQuickAddText] = useState<{
    [key: string]: string;
  }>({});

  // Per-appointment treatment suggestion add (inline in each group)
  const [treatmentQuickAddAppointmentId, setTreatmentQuickAddAppointmentId] = useState<number | null>(null);
  const [treatmentQuickAddForGeneral, setTreatmentQuickAddForGeneral] = useState(false);
  const [treatmentQuickAddText, setTreatmentQuickAddText] = useState('');

  // Use clinic info hook
  const { state, isLoading, addItem, updateItem, deleteItem } = useClinicInfo(
    patient?.id
  );

  // Generic add function
  const handleAddItem = (
    text: string,
    type:
      | 'complaint'
      | 'diagnosis'
      | 'treatment-suggestion'
      | 'future-suggestion',
    setInput: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (text.trim()) {
      addItem(text.trim(), type);
      setInput('');
    }
  };

  // Quick add functions
  const showQuickAdd = (type: string) => {
    setQuickAddVisible(prev => ({ ...prev, [type]: true }));
    setQuickAddText(prev => ({ ...prev, [type]: '' }));
  };

  const hideQuickAdd = (type: string) => {
    setQuickAddVisible(prev => ({ ...prev, [type]: false }));
    setQuickAddText(prev => ({ ...prev, [type]: '' }));
  };

  const handleQuickAdd = (
    type:
      | 'complaint'
      | 'diagnosis'
      | 'treatment-suggestion'
      | 'future-suggestion'
  ) => {
    const text = quickAddText[type] || '';
    if (text.trim()) {
      addItem(text.trim(), type);
      hideQuickAdd(type);
    }
  };

  const showTreatmentQuickAddForAppointment = (appointmentId: number | null) => {
    setTreatmentQuickAddAppointmentId(appointmentId);
    setTreatmentQuickAddForGeneral(appointmentId === null);
    setTreatmentQuickAddText('');
    setQuickAddVisible(prev => ({ ...prev, 'treatment-suggestion': false }));
  };

  const hideTreatmentQuickAddForAppointment = () => {
    setTreatmentQuickAddAppointmentId(null);
    setTreatmentQuickAddForGeneral(false);
    setTreatmentQuickAddText('');
  };

  const handleTreatmentOfferingSelect = (value: any, appointmentId: number | null) => {
    const offering = value?.data;
    if (!offering?.id) return;

    // Prevent duplicate offering for same appointment (or general suggestions when appointmentId is null)
    const existsForSameContext = state.treatmentSuggestions.some((item: any) => {
      const itemApptId = item.appointment_id || item.appointment?.id || null;
      const itemOfferingId = item.offering_id || item.offering?.id || null;
      return (
        itemApptId === (appointmentId ?? null) &&
        itemOfferingId === Number(offering.id)
      );
    });
    if (existsForSameContext) {
      hideTreatmentQuickAddForAppointment();
      if (appointmentId === null) {
        hideQuickAdd('treatment-suggestion');
      }
      return;
    }
    addItem(
      { id: Number(offering.id), name: offering.name, price: Number(offering.price) || 0 },
      'treatment-suggestion',
      appointmentId ?? undefined
    );
    hideTreatmentQuickAddForAppointment();
    if (appointmentId === null) hideQuickAdd('treatment-suggestion');
  };

  const formatCurrency = (amount: number) =>
    `₹ ${Number(amount || 0).toLocaleString('en-IN')}`;

  // Generic edit functions
  const startEdit = (
    id: number,
    text: string,
    type:
      | 'complaint'
      | 'diagnosis'
      | 'treatment-suggestion'
      | 'future-suggestion'
  ) => {
    setEditingId(id);
    setEditingText(text);
    setEditingType(type);
  };

  const saveEdit = (id: number) => {
    if (editingType) {
      updateItem(id, editingText, editingType);
      setEditingId(null);
      setEditingText('');
      setEditingType(null);
    }
  };

  // Render creator info
  const renderCreatorInfo = (
    item:
      | ComplaintItem
      | DiagnosisItem
      | TreatmentSuggestionItem
      | FutureSuggestionItem
  ) => {
    const creatorName =
      item.creator?.first_name && item.creator?.last_name
        ? `${item.creator.first_name} ${item.creator.last_name}`
        : 'Unknown';

    return (
      <Text type="secondary" style={{ fontSize: '10px', lineHeight: '12px' }}>
        {dayjs(item.created_at).format('DD MMM, YYYY')} by {creatorName}
      </Text>
    );
  };

  // Render list item
  const renderListItem = (
    item:
      | ComplaintItem
      | DiagnosisItem
      | TreatmentSuggestionItem
      | FutureSuggestionItem,
    type:
      | 'complaint'
      | 'diagnosis'
      | 'treatment-suggestion'
      | 'future-suggestion'
  ) => {
    const getText = () => {
      switch (type) {
        case 'complaint':
          return (item as ComplaintItem).description;
        case 'diagnosis':
          return (item as DiagnosisItem).details;
        case 'treatment-suggestion':
        case 'future-suggestion':
          return (item as TreatmentSuggestionItem | FutureSuggestionItem)
            .suggestion;
      }
    };

    const text = getText();
    const isEmpty = !text || text.trim() === '';

    const isTreatmentSuggestion = type === 'treatment-suggestion';

    // For treatment suggestions, use offering-style row: name + revenue + delete only
    if (isTreatmentSuggestion) {
      const anyItem: any = item;
      const label =
        anyItem.offering?.name ||
        text ||
        'Treatment suggestion';
      const rawPrice = anyItem.price ?? anyItem.offering?.price;
      const price =
        rawPrice !== undefined && rawPrice !== null
          ? Number(rawPrice)
          : undefined;

      const formatCurrency = (amount: number) =>
        `₹ ${Number(amount || 0).toLocaleString('en-IN')}`;

      return (
        <List.Item
          key={item.id}
          style={{ padding: '8px 12px' }}
          actions={[
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this item?"
              onConfirm={() => deleteItem(item.id, type)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ padding: '2px 4px' }}
              />
            </Popconfirm>,
          ]}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                flex: 1,
                fontSize: 12,
                lineHeight: '16px',
                color: '#262626',
                fontWeight: 500,
              }}
            >
              {label}
            </div>
            {price !== undefined && !Number.isNaN(price) && (
              <div
                style={{
                  minWidth: 90,
                  textAlign: 'right',
                  fontWeight: 700,
                  color: '#16a34a',
                  fontSize: 12,
                }}
              >
                {formatCurrency(price)}
              </div>
            )}
          </div>
        </List.Item>
      );
    }

    // Default rendering for other types
    return (
      <List.Item
        key={item.id}
        style={{ padding: '8px 12px' }}
        actions={
          !isEmpty
            ? [
                <Button
                  key="edit"
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => startEdit(item.id, text, type)}
                  style={{ padding: '2px 4px' }}
                />,
                <Popconfirm
                  key="delete"
                  title="Are you sure you want to delete this item?"
                  onConfirm={() => deleteItem(item.id, type)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ padding: '2px 4px' }}
                  />
                </Popconfirm>,
              ]
            : []
        }
      >
        <div style={{ width: '100%' }}>
          {editingId === item.id ? (
            <div>
              <Input
                size="small"
                value={editingText}
                onChange={e => setEditingText(e.target.value)}
                onPressEnter={() => saveEdit(item.id)}
                onBlur={() => saveEdit(item.id)}
                placeholder={isEmpty ? `Enter ${type}...` : ''}
                autoFocus
                style={{ marginBottom: isEmpty ? '4px' : '0' }}
              />
              {isEmpty && (
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => saveEdit(item.id)}
                    style={{ fontSize: '10px', height: '20px' }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      deleteItem(item.id, type);
                      setEditingId(null);
                      setEditingText('');
                      setEditingType(null);
                    }}
                    style={{ fontSize: '10px', height: '20px' }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size={2}>
              <Text style={{ fontSize: '12px', lineHeight: '16px' }}>
                {text || 'Empty item'}
              </Text>
              {/* Keep creator info for non-treatment items only */}
              {!isEmpty && !isTreatmentSuggestion && renderCreatorInfo(item)}
            </Space>
          )}
        </div>
      </List.Item>
    );
  };

  // Render section
  const renderSection = (
    title: string,
    items:
      | ComplaintItem[]
      | DiagnosisItem[]
      | TreatmentSuggestionItem[]
      | FutureSuggestionItem[],
    type:
      | 'complaint'
      | 'diagnosis'
      | 'treatment-suggestion'
      | 'future-suggestion',
    inputValue: string,
    setInputValue: React.Dispatch<React.SetStateAction<string>>,
    placeholder: string
  ) => (
    <Card
      styles={{
        body: {
          padding: '8px',
          height: getSectionHeight(),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ marginBottom: 0, height: getSectionHeight() }}
    >
      {/* Header with Title and Add Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 8,
        padding: '4px 0',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Text strong style={{ fontSize: '14px', color: '#262626' }}>
          {title}
        </Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showQuickAdd(type)}
          style={{ 
            height: '24px', 
            fontSize: '12px',
            padding: '0 6px',
            minWidth: 'auto'
          }}
          size="small"
        >
          Add {title}
        </Button>
      </div>

      {/* Quick Add Input Area (appears at top when Add is clicked) */}
      {quickAddVisible[type] && (
        <div style={{ marginBottom: 8, flexShrink: 0, padding: '0 4px' }}>
          <Input.TextArea
            value={quickAddText[type] || ''}
            onChange={e => setQuickAddText(prev => ({ ...prev, [type]: e.target.value }))}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows: 2 }}
            style={{ marginBottom: 6, fontSize: '12px', width: '100%' }}
            onPressEnter={() => handleQuickAdd(type)}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button
              type="primary"
              onClick={() => handleQuickAdd(type)}
              style={{ 
                flex: 1,
                height: '24px', 
                fontSize: '10px'
              }}
              size="small"
            >
              Add
            </Button>
            <Button
              onClick={() => hideQuickAdd(type)}
              style={{ 
                height: '24px', 
                fontSize: '10px'
              }}
              size="small"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <List
        size="small"
        bordered
        dataSource={items as any[]}
        renderItem={item => renderListItem(item, type)}
        style={{
          backgroundColor: '#fff',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          fontSize: '12px',
        }}
      />
    </Card>
  );

  // Special render for Treatment Suggestions: group by appointment-wise
  const renderTreatmentSuggestionsSection = () => {
    // Group treatment suggestions by appointment (always include General Suggestions so its Add button is always available)
    const groups = new Map<
      string,
      {
        title: string;
        subtitle?: string;
        items: TreatmentSuggestionItem[];
      }
    >();

    // Ensure General Suggestions group always exists so "Add Treatment Suggestion" can open
    // groups.set('no_appointment', {
    //   title: 'General Suggestions',
    //   items: [],
    // });

    state.treatmentSuggestions.forEach(item => {
      const anyItem: any = item;
      const apptId = anyItem.appointment_id || anyItem.appointment?.id;
      const createdAt = anyItem.appointment?.scheduled_datetime;

      const key = apptId ? String(apptId) : 'no_appointment';

      if (!groups.has(key)) {
        const title = apptId
          ? `Appointment #${apptId}`
          : 'General Suggestions';
        const subtitle =
          apptId && createdAt
            ? dayjs(createdAt).format('DD MMM, YYYY')
            : undefined;

        groups.set(key, {
          title,
          subtitle,
          items: [],
        });
      }

      groups.get(key)!.items.push(item);
    });

    const groupArray = Array.from(groups.entries()).sort((a, b) => {
      // Sort by appointment id descending; general group at bottom
      if (a[0] === 'no_appointment') return 1;
      if (b[0] === 'no_appointment') return -1;
      return Number(b[0]) - Number(a[0]);
    });

    return (
      <Card
        styles={{
          body: {
            padding: '8px',
            height: getSectionHeight(),
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        style={{ marginBottom: 0, height: getSectionHeight() }}
      >
        {/* Header with Title and Add Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            padding: '4px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Text strong style={{ fontSize: '14px', color: '#262626' }}>
            Treatment Suggestions
          </Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showQuickAdd('treatment-suggestion')}
            style={{
              height: '24px',
              fontSize: '12px',
              padding: '0 6px',
              minWidth: 'auto',
            }}
            size="small"
          >
            Add Treatment Suggestions
          </Button>
        </div>

        {/* Quick Add: offering dropdown (top-level) */}
        {quickAddVisible['treatment-suggestion'] && treatmentQuickAddAppointmentId === null && (
          <div style={{ marginBottom: 8, flexShrink: 0, padding: '0 4px' }}>
            <SearchSelectField
              resource="offerings"
              optionValue="id"
              searchFields={['name', 'description', 'category']}
              searchDebounceMs={300}
              filters={[
                { field: 'category', operator: 'in' as const, value: ['TREATMENT', 'LHR', 'PACKAGE', 'OTHER'] },
                { field: 'is_active', operator: 'in' as const, value: [1] },
              ]}
              sorters={[{ field: 'name', order: 'asc' }]}
              pagination={{ pageSize: 20 }}
              queryOptions={{ enabled: true, staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false, refetchOnMount: true }}
              placeholder="Select offering..."
              value={null}
              onChange={val => handleTreatmentOfferingSelect(val, null)}
              initialValue={null}
              optionRender={(option: any) => `${option?.name || ''} (₹${option?.price ?? 0})`}
              allowClear
              showSearch
              style={{ width: '100%' }}
            />
            <Button
              onClick={() => hideQuickAdd('treatment-suggestion')}
              size="small"
              style={{ marginTop: 6, fontSize: '10px' }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Grouped suggestions by appointment */}
        <div
          style={{
            backgroundColor: '#fff',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            fontSize: '12px',
            padding: '0 2px',
          }}
        >
          {groupArray.length === 0 ? (
            <div style={{ padding: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No treatment suggestions added yet.
              </Text>
            </div>
          ) : (
            groupArray.map(([key, group]) => {
              const appointmentIdForGroup = key === 'no_appointment' ? null : Number(key);
              const isThisGroupAddOpen =
                (key === 'no_appointment' && treatmentQuickAddForGeneral) ||
                (key !== 'no_appointment' && treatmentQuickAddAppointmentId === Number(key));

              return (
                <div key={key} style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      marginBottom: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <div>
                      <Text strong style={{ fontSize: 12, color: '#262626' }}>
                        {group.title}
                      </Text>
                      {group.subtitle && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#8c8c8c',
                          }}
                        >
                          {group.subtitle}
                        </div>
                      )}
                    </div>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        showTreatmentQuickAddForAppointment(appointmentIdForGroup)
                      }
                      style={{
                        height: '22px',
                        fontSize: '11px',
                        padding: '0 6px',
                        minWidth: 'auto',
                        flexShrink: 0,
                      }}
                      size="small"
                    >
                      Add Treatment Suggestion
                    </Button>
                  </div>

                  {/* Inline quick-add: offering dropdown for this appointment */}
                  {isThisGroupAddOpen && (
                    <div
                      style={{
                        marginBottom: 8,
                        padding: '6px 8px',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <SearchSelectField
                        resource="offerings"
                        optionValue="id"
                        searchFields={['name', 'description', 'category']}
                        searchDebounceMs={300}
                        filters={[
                          { field: 'category', operator: 'in' as const, value: ['TREATMENT', 'LHR', 'PACKAGE', 'OTHER'] },
                          { field: 'is_active', operator: 'in' as const, value: [1] },
                        ]}
                        sorters={[{ field: 'name', order: 'asc' }]}
                        pagination={{ pageSize: 20 }}
                        queryOptions={{ enabled: true, staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false, refetchOnMount: true }}
                        placeholder="Select offering..."
                        value={null}
                        onChange={val => handleTreatmentOfferingSelect(val, appointmentIdForGroup)}
                        initialValue={null}
                        optionRender={(option: any) => `${option?.name || ''} (₹${option?.price ?? 0})`}
                        allowClear
                        showSearch
                        style={{ width: '100%' }}
                      />
                      <Button
                        onClick={hideTreatmentQuickAddForAppointment}
                        size="small"
                        style={{ marginTop: 6, fontSize: '10px' }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}

                  <List
                    size="small"
                    bordered
                    dataSource={group.items as any[]}
                    renderItem={item =>
                      renderListItem(item, 'treatment-suggestion')
                    }
                    style={{
                      backgroundColor: '#fff',
                      marginBottom: 4,
                    }}
                  />

                  {/* Estimated total for this appointment */}
                  {group.items.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 4,
                        paddingTop: 4,
                        borderTop: '1px solid #f0f0f0',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#16a34a',
                      }}
                    >
                      Estimated total: {formatCurrency(
                        group.items.reduce(
                          (sum, item) =>
                            sum + (Number((item as any).price ?? (item as any).offering?.price) || 0),
                          0
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 12,
        height: 'calc(100vh - 200px)',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 200px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Left panel - 4 sections in a 2x2 grid */}
        <div
          style={{
            flex: '1 1 66%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            alignContent: 'start',
            minWidth: 0,
            overflowY: 'auto',
            maxHeight: '100%',
          }}
        >
          {renderSection(
            'Complaints',
            state.complaints,
            'complaint',
            complaintInput,
            setComplaintInput,
            'Enter complaint...'
          )}
          {renderTreatmentSuggestionsSection()}
          {renderSection(
            'Diagnosis',
            state.diagnosis,
            'diagnosis',
            diagnosisInput,
            setDiagnosisInput,
            'Enter diagnosis...'
          )}
          {renderSection(
            'Future Suggestions',
            state.futureSuggestions,
            'future-suggestion',
            futureSuggestionInput,
            setFutureSuggestionInput,
            'Enter future suggestion...'
          )}
        </div>

        {/* Right panel - Patient Chat */}
        <div
          style={{
            flex: '0 0 33%',
            minWidth: 280,
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatComponent
            type="patient"
            relativeId={patient?.id || 0}
            patientId={patient?.id}
            title="Patient Chat"
            placeholder="Type a message to the patient..."
            height="100%"
            isintialScroll={true}
          />
        </div>
      </div>
    </div>
  );
};
